import axios, { AxiosError } from 'axios'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { get, set } from 'lodash'
import useEffectiveAuthToken from '@/composables/useEffectiveAuthToken'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    skipRedirectOn403?: boolean;
  }
}

export interface ErrorResponse extends AxiosError {
  errorMsg: string,
  errors: Array<string>
}

interface ErrorData {
  error?: string,
  detail?: string,
  reason?: string
}

export default defineNuxtPlugin(() => {
  const { getEffectiveAuthToken } = useEffectiveAuthToken()
  const defaultUrl = (<string>import.meta.env.VITE_API_ENDPOINT)

  const http = axios.create({
    baseURL: defaultUrl,
    headers: {
      common: {
        'Content-Language': 'en',
        'X-Ignore-Session': true
      }
    }
  })

  const requestInterceptor = {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      if (
        import.meta.env.ENVIROMENT === 'development' &&
        http.defaults.baseURL === '/'
      ) {
        config.url = `/api${config.url}`
      }
      const authToken = await getEffectiveAuthToken()
      if (authToken) {
        config.headers.Authorization = authToken
      }
      return config
    },
    onRejected: (error: AxiosError) => {
      throw error
    }
  }

  const responseInterceptor = {
    onFulfilled: (response: AxiosResponse) => {
      if (get(response, 'status') === 403 || get(response, 'status') === 401) {
        return navigateTo('/logout') as Promise<void | any>
      }
      return response
    },
    onRejected: (error: ErrorResponse) => {
      if (error.code === 'ERR_NETWORK') {
        error.errorMsg = error.message
        error.errors = [error.message]
        throw error
      }
      const route = useRoute()
      const { response } = error
      const data = (response as AxiosResponse<ErrorData>)?.data
      const code = get(response, 'status', 0)
      const hasToSkipRedirection = route.name === 'login' || response?.config?.skipRedirectOn403
      const hasToRedirect = code === 401 || data?.detail === 'Error decoding token.'
      const hasToRedirectToStatus = data?.reason === 'suspended' && route.name !== 'logout'
      if (hasToRedirect && !hasToSkipRedirection) {
        if (hasToRedirectToStatus) {
          navigateTo('/account-status')
        } else {
          navigateTo({ path: '/logout', query: route?.query })
        }
      } else if (code === 403 && !hasToSkipRedirection) {
        navigateTo('/invalid-url')
      } else if (code === 400) {
        const errors = data ? Object.values(data)?.flat() : []
        error.errorMsg = errors?.join(', ')
        error.errors = errors
      } else {
        error.errors = []
        const errorMessage = data?.error
        if (errorMessage) {
          error.errors.push(errorMessage)
        }
      }
      throw error
    }
  }

  http.interceptors.request.use(
    requestInterceptor.onFulfilled,
    requestInterceptor.onRejected
  )

  http.interceptors.response.use(
    responseInterceptor.onFulfilled,
    responseInterceptor.onRejected
  )

  set(http, '$get', (url: string, ...params: any[]) => {
    return http.get(url, ...params).then(response => response.data)
  })

  set(http, '$post', (url: string, ...params: any[]) => {
    return http.post(url, ...params).then(response => response.data)
  })

  set(http, '$patch', (url: string, ...params: any[]) => {
    return http.patch(url, ...params).then(response => response.data)
  })

  set(http, '$delete', (url: string, ...params: any[]) => {
    return http.delete(url, ...params).then(response => response.data)
  })

  return {
    provide: {
      axios: http
    }
  }
})
